// Ahead of costs or beyond them (ZEN-804, ZEN-806, ZEN-807). The server splits
// what is withdrawn beyond qualified costs into the part later costs of the
// same tax year can absorb (ahead, amber) and the rest (beyond, red), and sends
// the costs still to come. Every screen that shows these reads this file, so
// the Overview card, the withdrawal form and the Higher Education card agree.
const round2 = (v) => Math.round((Number(v) + Number.EPSILON) * 100) / 100;
const num = (v) => (Number.isFinite(Number(v)) ? Number(v) : 0);

// `setAside` is a withdrawal being edited: its amount is already in the
// server's total, so it comes off first, from the beyond part first.
// A server that sends no split shows everything as beyond (red), never amber.
// `awaiting` is money covered by costs already paid whose exchange rate is not
// final yet (ZEN-835): amber like ahead, taken before it, as the server does.
export function overWithdrawnSplit(aqee, setAside = 0) {
  const over = round2(Math.max(0, num(aqee?.over_withdrawn_by) - num(setAside)));
  if (!(over > 0.005)) return { over: 0, awaiting: 0, ahead: 0, beyond: 0 };
  const awaiting = round2(Math.min(over, Math.max(0, num(aqee?.withdrawn_awaiting_rate))));
  const ahead = round2(Math.min(over - awaiting, Math.max(0, num(aqee?.withdrawn_ahead_of_costs))));
  return { over, awaiting, ahead, beyond: round2(over - awaiting - ahead) };
}

// What one beneficiary's 529 Withdrawals card leads with, for the card and the
// All beneficiaries table alike (ZEN-835, Maryse 2 Nov 2026): red when any
// money is beyond the year's qualified costs, amber when it is all ahead or
// waiting for a rate, green otherwise.
export function withdrawalsHeadline(aqee) {
  const s = overWithdrawnSplit(aqee);
  const kind = s.beyond > 0.005 ? "beyond"
    : s.ahead > 0.005 ? "ahead"
    : s.awaiting > 0.005 ? "awaiting"
    : "safe";
  const amount = kind === "beyond" ? s.beyond : kind === "ahead" ? s.ahead
    : kind === "awaiting" ? s.awaiting : round2(Math.max(0, num(aqee?.safe_to_withdraw)));
  return { kind, amount, ...s };
}

// The words, one place (Maryse 25 Oct and 2 Nov 2026).
export const AWAITING_RATE_SENTENCE = "Already paid. It counts as soon as the exchange rate is final.";
export function headlineWords(kind, year) {
  if (kind === "beyond") return `beyond ${year} qualified costs`;
  if (kind === "ahead") return "withdrawn ahead of costs";
  if (kind === "awaiting") return "waiting for a final exchange rate";
  return "safe to withdraw";
}

// Whether the server sends the costs still to come (ZEN-807). An older server
// does not; the screens then show what they showed before, never a half-sum.
export const hasStillToCome = (aqee) => aqee?.qualified_still_to_come != null;

// The lines under the card (ZEN-807, Maryse 26 Oct 2026): already paid, + still
// to come, = the year total, − scholarships, − AOTC reserve, − already
// withdrawn, = still safe or over by. Scholarships and the reserve show only
// the part the year's costs use, so the lines land on the true figure.
export function yearLines(aqee) {
  const paid = round2(num(aqee?.total_qualified));
  const awaitingRate = round2(Math.max(0, num(aqee?.qualified_awaiting_rate)));
  const toCome = round2(Math.max(0, num(aqee?.qualified_still_to_come)));
  const yearTotal = round2(paid + awaitingRate + toCome);
  const aid = round2(Math.min(Math.max(0, num(aqee?.tax_free_assistance)), yearTotal));
  const reserve = round2(Math.min(Math.max(0, num(aqee?.aotc_llc_reserved)), yearTotal - aid));
  const withdrawn = round2(num(aqee?.total_withdrawn));
  const result = round2(yearTotal - aid - reserve - withdrawn);
  return { paid, awaitingRate, toCome, yearTotal, aid, reserve, withdrawn, result };
}

// The Higher Education card (ZEN-807): the same figures as the Safe to withdraw
// card, so the two always show the same room. While nothing is over, room is
// against costs already paid (the green card's figure). Once money is withdrawn
// ahead, costs still to come join the year total. Scholarships and the AOTC
// reserve come off qualified, and are named on the second line.
export function higherEdRoom(aqee) {
  // Both figures or neither: without the split this card keeps its own.
  if (!hasStillToCome(aqee) || aqee?.withdrawn_ahead_of_costs == null) return null;
  const L = yearLines(aqee);
  const { over, beyond } = overWithdrawnSplit(aqee);
  if (!(over > 0.005)) {
    const qualified = round2(Math.max(0, L.paid - Math.min(L.aid + L.reserve, L.paid)));
    return { qualified, withdrawn: L.withdrawn, room: round2(qualified - L.withdrawn), lines: null };
  }
  const qualified = round2(L.yearTotal - L.aid - L.reserve);
  // Red exactly when the split says some money is beyond costs.
  const room = beyond > 0.005 ? round2(-beyond) : round2(Math.max(0, L.result));
  return { qualified, withdrawn: L.withdrawn, room,
    lines: { paid: L.paid, awaitingRate: L.awaitingRate, toCome: L.toCome, aid: L.aid, reserve: L.reserve } };
}
